import { CollapseGroup, Tooltip } from 'uikit';
import type { CollapseGroupProps } from 'uikit';
import { pickProps } from 'utils';
import { useOnRefAttributeChange } from 'utils/dom';

import type React from 'react';
import { useMemo, useRef } from 'react';
import type { Control } from 'react-hook-form';
import { useFormState } from 'react-hook-form';

export interface ICollapseFieldsToCheckItem {
    index: number;
    fields: Array<Array<number | string> | string | number>;
    errorMessage: string;
}

type TControlledCollapseGroup = CollapseGroupProps<any, any> & {
    control: Control;
    fieldsToCheck?: Array<ICollapseFieldsToCheckItem>;
    classNameTooltip?: string;
    onTooltipAttributeChange?: (attributeName: string, target: Node) => void;
    labelWithLessWidth?: boolean;
};

interface IControlledCollapseLabel {
    label: string;
    isError?: boolean;
    errorMessage: string;
    className?: string;
    onTooltipAttributeChange?: (attributeName: string, target: Node) => void;
    labelWithLessWidth?: boolean;
}

const ControlledCollapseLabel: React.FC<IControlledCollapseLabel> = (props) => {
    const { label, isError, errorMessage, className, onTooltipAttributeChange, labelWithLessWidth } = props;

    const ref = useRef(null);

    const tooltipRef = useRef<HTMLDivElement>(null);

    useOnRefAttributeChange({
        targetRef: tooltipRef,
        onAttributeChange: onTooltipAttributeChange,
    });

    return (
        <div ref={ref} className={labelWithLessWidth ? 'labelWithLessWidth' : ''}>
            {label}
            <span className="FieldLabel-Star">*</span>
            {isError && (
                <Tooltip
                    size="s"
                    direction="rightCenter"
                    status="alert"
                    anchorRef={ref}
                    className={className}
                    ref={tooltipRef}
                >
                    {errorMessage}
                </Tooltip>
            )}
        </div>
    );
};

ControlledCollapseLabel.displayName = 'ControlledCollapseLabel';

export const ControlledCollapseGroup: React.FC<TControlledCollapseGroup> = (props) => {
    const {
        items,
        control,
        fieldsToCheck,
        classNameTooltip,
        onTooltipAttributeChange,
        labelWithLessWidth = false,
    } = props;
    const { errors } = useFormState({ control });

    const invalidIndexes = fieldsToCheck
        ?.filter((f) => {
            const errValues = Object.values(pickProps(errors, f.fields));

            return !!errValues.length;
        })
        .map((f) => f.index);

    const actualItems = useMemo(
        () =>
            items?.map((item, index) => {
                const currentFieldsToCheck = fieldsToCheck?.find((f) => f.index === index);

                if (currentFieldsToCheck) {
                    return {
                        ...item,
                        label: (
                            <ControlledCollapseLabel
                                label={item.label}
                                isError={invalidIndexes?.includes(index)}
                                errorMessage={currentFieldsToCheck.errorMessage}
                                className={classNameTooltip}
                                onTooltipAttributeChange={onTooltipAttributeChange}
                                labelWithLessWidth={labelWithLessWidth}
                            />
                        ),
                    };
                }

                return item;
            }),
        [classNameTooltip, fieldsToCheck, invalidIndexes, items, labelWithLessWidth, onTooltipAttributeChange],
    );

    const opened = useMemo(() => items.map((_, index) => index), [items]);

    return <CollapseGroup items={actualItems} opened={opened} />;
};

ControlledCollapseGroup.displayName = 'ControlledCollapseGroup';
